import {
  hasAppRenderStarted,
  isAppMounted,
  showMountFailure,
} from './showMountFailure'

let handlersInstalled = false

/**
 * Origin the built app scripts are served from in production and staging.
 * Assets are uploaded to the static CDN, so `event.filename` and stack frames
 * reference this origin rather than the page origin.
 *
 * @see `renderBuiltUrl` in vite.config.mjs
 */
const APP_ASSET_ORIGIN = 'https://static.buffer.com'
const BOOT_TIMEOUT_MESSAGE =
  'Buffer is taking too long to load. Check your internet connection and refresh, or try updating your browser.'

/**
 * The page origin covers the dev server, where assets are emitted with
 * relative URLs.
 */
function getAppScriptOrigins(): readonly string[] {
  return [window.location.origin, APP_ASSET_ORIGIN]
}

/**
 * Ignore cross-origin script errors (empty filename / "Script error.") and
 * extension-injected scripts so we do not swap the spinner for a false
 * failure message on an otherwise healthy session.
 */
export function isAppScriptError(event: ErrorEvent): boolean {
  const { filename } = event
  if (!filename) {
    return false
  }

  try {
    const { origin } = new URL(filename, window.location.href)
    return getAppScriptOrigins().includes(origin)
  } catch {
    return false
  }
}

/**
 * Rejections from third-party scripts or extension-injected code lack an
 * app-script stack frame. Require one before treating boot as failed.
 */
export function isAppScriptUnhandledRejection(
  event: PromiseRejectionEvent,
): boolean {
  const { reason } = event
  if (!(reason instanceof Error)) {
    return false
  }

  const stack = reason.stack
  if (!stack) {
    return false
  }

  return getAppScriptOrigins().some((origin) => stack.includes(origin))
}

/**
 * Boot-window handlers only: once the app has mounted they go inert, so
 * post-mount errors stay with the React error boundary.
 *
 * No Sentry capture here: `initSentry()` runs before these handlers install
 * (see `~publish/boot/installPreMountErrorHandlers`) and the SDK's default
 * global handlers already capture uncaught errors and unhandled rejections.
 * These listeners only decide whether to swap the boot spinner for the
 * failure screen.
 */
export function setupPreMountErrorHandlers(): void {
  if (handlersInstalled || typeof window === 'undefined') {
    return
  }

  handlersInstalled = true

  window.addEventListener('error', (event: ErrorEvent) => {
    if (isAppMounted()) {
      return
    }

    if (isAppScriptError(event)) {
      showMountFailure()
    }
  })

  window.addEventListener(
    'unhandledrejection',
    (event: PromiseRejectionEvent) => {
      // Once render has started, genuine boot failures surface as `error`
      // events (React re-throws render-phase crashes); an unhandled rejection
      // at that point is more likely a fire-and-forget side effect that a
      // healthy boot would survive, so leave it to Sentry instead of
      // replacing the booting app with the failure screen.
      if (isAppMounted() || hasAppRenderStarted()) {
        return
      }

      if (isAppScriptUnhandledRejection(event)) {
        showMountFailure()
      }
    },
  )

  window.addEventListener('publishbootwatchdogtimeout', () => {
    if (isAppMounted()) {
      return
    }

    showMountFailure(BOOT_TIMEOUT_MESSAGE)
  })
}
